/** Device and install-state detection used by sign-in, notification setup and the guides. */

export type Platform = 'ios' | 'android' | 'windows' | 'mac' | 'linux' | 'other';

export function detectPlatform(ua = navigator.userAgent): Platform {
  if (/iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1))
    return 'ios';
  if (/Android/i.test(ua)) return 'android';
  if (/Windows/i.test(ua)) return 'windows';
  if (/Macintosh|Mac OS X/i.test(ua)) return 'mac';
  if (/Linux/i.test(ua)) return 'linux';
  return 'other';
}

/** True when running as an installed app (Home Screen on iPhone, installed PWA on desktop). */
export function isStandalone(): boolean {
  const nav = navigator as Navigator & { standalone?: boolean };
  return nav.standalone === true || window.matchMedia('(display-mode: standalone)').matches;
}

/** iOS major.minor version, or null when not iOS. */
export function iosVersion(ua = navigator.userAgent): number | null {
  const m = /OS (\d+)[._](\d+)/.exec(ua);
  if (!m || detectPlatform(ua) !== 'ios') return null;
  return Number(`${m[1]}.${m[2]}`);
}

export function isEdge(ua = navigator.userAgent): boolean {
  return /Edg\//.test(ua);
}

export function pushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

export function browserName(ua = navigator.userAgent): string {
  if (/Edg\//.test(ua)) return 'Edge';
  if (/CriOS|Chrome\//.test(ua)) return 'Chrome';
  if (/Firefox\//.test(ua)) return 'Firefox';
  if (/Safari\//.test(ua)) return 'Safari';
  return 'Browser';
}

export function deviceLabel(ua = navigator.userAgent): string {
  const p = detectPlatform(ua);
  const name = {
    ios: 'iPhone',
    android: 'Android',
    windows: 'Windows',
    mac: 'Mac',
    linux: 'Linux',
    other: 'Device',
  }[p];
  return `${name} · ${browserName(ua)}`;
}
